'use client'

import BookImage from '@components/BookImage'
import { BookVersionsModal } from '@components/BookVersionsModal'
import { BookDto } from '@hermes/domains'
import { ShippingPrice } from '@hermes/types'
import { Container } from '@hermes/web-components'
import { useLocale } from '@hooks/useLocale'
import { useAnalytics } from '@lib/contexts/analyticsContext'
import { getCurrencyFromLocale } from '@lib/utils/getCurrencyFromLocale'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import { getStage } from '@lib/utils/getStage'
import { setRecentViewedVariant } from '@lib/utils/localStorageHelpers'
import React, { useEffect, useRef, useState } from 'react'
import { useBookDetails } from '@hermes/api-sdk'
import ActionSection from './ActionSection'
import DetailsSection from './DetailsSection'
import { BookDetailsMarkup, BookImageContainer } from './ProductPage.styles'
import { SuggestionsSection } from './SuggestionsSection'

type ProductPageProps = {
  bookDetails: BookDto
  shippingPrice: ShippingPrice
}

export const ProductPage: React.FC<ProductPageProps> = ({ bookDetails, shippingPrice }) => {
  const locale = useLocale()
  const currency = getCurrencyFromLocale(locale)
  const analytics = useAnalytics()
  const region = getRegionFromLocale(locale)
  const stage = getStage()
  const { data: bookWithVersions } = useBookDetails({
    bookId: bookDetails.id,
    includeVersions: true
  })

  const [versionsModalShow, setVersionsModalShow] = useState(false)
  const navigationRefs = {
    about: useRef<HTMLDivElement>(null),
    versions: useRef<HTMLDivElement>(null),
    reviews: useRef<HTMLDivElement>(null),
    mightLike: useRef<HTMLDivElement>(null)
  }

  useEffect(() => {
    setRecentViewedVariant(bookDetails, region, stage)
  }, [])

  useEffect(() => {
    analytics.trackEvent('PRODUCT_VIEWED', {
      productId: bookDetails.id,
      price: bookDetails.availability.totalPrice || 0,
      currency,
      productName: bookDetails.title
    })
  }, [])

  return (
    <Container maxWidth="xl">
      <BookDetailsMarkup>
        <BookImageContainer dominantColor={bookDetails.imageDominateColor}>
          <BookImage
            key={bookDetails.id}
            src={bookDetails.fullImage || bookDetails.smallImage}
            priority
            width={350}
            height={535}
            style={{
              objectFit: 'contain'
            }}
            alt={bookDetails.title}
          />
        </BookImageContainer>
        <ActionSection
          shippingPrice={shippingPrice}
          bookDetails={bookDetails}
          setVersionsModalShow={setVersionsModalShow}
          navigationRefs={navigationRefs}
          versions={bookWithVersions?.versions}
        />
        <DetailsSection
          versionsRef={navigationRefs.versions}
          bookDetails={bookDetails}
          aboutRef={navigationRefs.about}
          setVersionsModalShow={setVersionsModalShow}
          reviewsRef={navigationRefs.reviews}
        />
      </BookDetailsMarkup>
      <SuggestionsSection bookId={bookDetails.id} mightLikeRef={navigationRefs.mightLike} />
      {bookWithVersions?.versions && (
        <BookVersionsModal
          isOpen={versionsModalShow}
          onClose={() => setVersionsModalShow(false)}
          versions={bookWithVersions?.versions}
        />
      )}
    </Container>
  )
}
